// System Status — Operations Deck style
// "LIVE" pill + rows w/ uptime bars. Values are static for now (no health API yet).

interface StatusRow {
  label: string;
  state: 'Operational' | 'Connected' | 'Available' | 'Degraded' | 'Down';
  latency?: string;
  uptime?: number; // percent
}

const DEFAULT_ROWS: StatusRow[] = [
  { label: 'API Server',      state: 'Operational', latency: '—', uptime: 99.98 },
  { label: 'Database',        state: 'Connected',   latency: '—', uptime: 99.99 },
  { label: '3D Model Viewer', state: 'Available',   latency: '—', uptime: 99.62 },
  { label: 'Auth',            state: 'Operational', latency: '—', uptime: 100.0 },
];

const stateColor = (state: StatusRow['state']) => {
  switch (state) {
    case 'Operational':
    case 'Connected':
    case 'Available':
      return 'var(--color-good)';
    case 'Degraded':
      return 'var(--color-warn)';
    case 'Down':
      return 'var(--color-bad)';
  }
};

interface Props {
  rows?: StatusRow[];
  title?: string;
  subtitle?: string;
}

export function SystemStatusPanel({
  rows = DEFAULT_ROWS,
  title = 'All systems go',
  subtitle = 'System',
}: Props) {
  return (
    <div
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        padding: 22,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              fontWeight: 500,
              color: 'var(--color-text-dim)',
            }}
          >
            {subtitle}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: -0.4,
              color: 'var(--color-text)',
              marginTop: 2,
            }}
          >
            {title}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 10.5,
            color: 'var(--color-good)',
            fontWeight: 600,
            background: 'rgba(26,143,90,0.08)',
            padding: '4px 9px',
            borderRadius: 999,
            fontFamily: 'var(--font-mono)',
            letterSpacing: 0.6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: 'var(--color-good)',
              display: 'inline-block',
            }}
          />
          LIVE
        </div>
      </div>

      {rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            padding: '10px 0',
            borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: stateColor(r.state),
                marginRight: 10,
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1, fontSize: 13, color: 'var(--color-text)' }}>{r.label}</span>
            <span style={{ fontSize: 11.5, color: 'var(--color-text-mid)', marginRight: 12 }}>{r.state}</span>
            {r.latency && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--color-text-dim)',
                  width: 50,
                  textAlign: 'right',
                }}
              >
                {r.latency}
              </span>
            )}
          </div>
          {typeof r.uptime === 'number' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginLeft: 16 }}>
              <div
                style={{
                  flex: 1,
                  height: 3,
                  background: 'var(--color-border)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${r.uptime}%`,
                    height: '100%',
                    background: r.uptime >= 99.9 ? 'var(--color-good)' : 'var(--color-warn)',
                  }}
                />
              </div>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--color-text-dim)',
                  width: 56,
                  textAlign: 'right',
                }}
              >
                {r.uptime.toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
